import { useState, useRef, useCallback, useEffect } from "react";
import { useParams, useNavigate } from "@tanstack/react-router";
import {
  LuHistory,
  LuShieldCheck,
  LuFileText,
  LuSend,
  LuRefreshCw,
} from "react-icons/lu";
import LayoutContentArea from "@/components/layout/LayoutContentArea";
import Breadcrumb from "@/components/shared/BreadCrumb";
import Loader from "@/components/shared/Loader";
import CertificationHeading from "@/components/certifications/CertificationsHeading";
import RevisionsTab from "@/components/certifications/Tabs/RevisionsTab";
import ValidationsTab from "@/components/certifications/Tabs/ValidationsTab";
import ReportsTab from "@/components/certifications/Tabs/ReportsTab";
import SubmissionTab from "@/components/certifications/Tabs/SubmissionTab";
import LifecycleTab from "@/components/certifications/Tabs/LifecycleTab";
import useCertificationPage from "@/components/certifications/Hooks/useCertificationPage";
import { useCertificationStep } from "@/components/certifications/Hooks/useCertificationStep";
import { useCSPPIDMatches } from "@/queries";
import { MatchLEAStatus } from "@/models/mid";
import { startOfLocalDay, endOfLocalDay } from "@/utils/dates";

const TABS = [
  { key: "validations", label: "Validations", icon: LuShieldCheck },
  { key: "revisions", label: "Revisions", icon: LuHistory },
  { key: "reports", label: "Reports", icon: LuFileText },
  { key: "submission", label: "Submission", icon: LuSend },
  { key: "lifecycle", label: "Lifecycle", icon: LuRefreshCw },
] as const;

type TabKey = (typeof TABS)[number]["key"];
const validTabKeys = TABS.map((t) => t.key) as readonly TabKey[];

function getTabFromHash(): TabKey {
  const hash = window.location.hash.replace("#", "");
  return validTabKeys.includes(hash as TabKey)
    ? (hash as TabKey)
    : "validations";
}

function formatReviewDate(raw: string): React.ReactNode {
  const hasTimezone = /Z|[+-]\d{2}:?\d{2}$/.test(raw);
  const d = new Date(hasTimezone ? raw : raw + "Z");
  if (isNaN(d.getTime())) return "N/A";
  const date = d.toLocaleDateString("en-US");
  const time = d.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
  return (
    <span
      style={{ display: "inline-flex", alignItems: "center", gap: "0.3rem" }}
    >
      <span>{date}</span>
      <span style={{ fontSize: "0.4rem", lineHeight: 1 }}>●</span>
      <span>{time}</span>
    </span>
  );
}

function deriveReportMonthYear(certificationid: string): string | null {
  const match = certificationid.match(/^MNTH(\d{4})(\d{2})$/);
  if (!match) return null;
  const [, year, month] = match;
  return `${year} ${month}`;
}

export default function CertificationPage() {
  const params = useParams({
    from: "/$leaid/_error-wrapper/certification-cycles/$certificationid",
  });
  const leaid = params.leaid as string;
  const certificationid = params.certificationid as string;
  const navigate = useNavigate();
  const { data: matchesData } = useCSPPIDMatches(leaid);

  const [activeTab, setActiveTabState] = useState<TabKey>(getTabFromHash);
  const [localReadSet, setLocalReadSet] = useState<Set<string>>(new Set());

  const handleReportRead = useCallback((id: string) => {
    setLocalReadSet((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }, []);

  const handleReportUnread = useCallback((id: string) => {
    setLocalReadSet((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const {
    esiidPath,
    leaContext,
    canCertify,
    canEditRecord,
    certifications,
    certification,
    certId,
    esiid,
    certStats,
    certificationMonth,
    certificationStatus,
    lockedAnomalyRunId,
    hasLockedRevision,
    mostRecentRevisionId,
    mostRecentReportingHistoryId,
    lockedReportingHistoryId,
    totalErrors,
    totalWarnings,
    canReviewReports,
    persistedReadIds,
    certLogsIsPending,
    isCycleEnded,
    discrepancies,
    reportIdToIndex,
    reportItems,
    handleAutoLock,
    handleReportViewed,
    handleCertify,
    handleDecertify,
  } = useCertificationPage(leaid, certificationid);

  useEffect(() => {
    if (!lockedAnomalyRunId) {
      setLocalReadSet((prev) => (prev.size === 0 ? prev : new Set()));
    }
  }, [lockedAnomalyRunId]);

  const readSet = new Set([...persistedReadIds, ...localReadSet]);
  const allReportsVerified =
    reportItems.length > 0 &&
    reportItems.every((r) => readSet.has(r.reportId.toString()));

  const ssidErrorsCount = (discrepancies?.discrepancies ?? []).filter(
    (a: any) => a.ErrorCode?.startsWith("SSID")
  ).length;

  const midsCount = (matchesData ?? []).filter(
    (m) => m.Status === MatchLEAStatus.UNRESOLVED_ACTIVE_LEA
  ).length;

  const { certificationStep } = useCertificationStep({
    certificationStatus,
    totalErrors,
    hasLockedRevision,
    allReportsVerified,
  });

  const reportMonthYear = deriveReportMonthYear(certificationid);

  const wrappedHandleDecertify = useCallback(async () => {
    const success = await handleDecertify();
    if (success) setLocalReadSet(new Set());
    return success;
  }, [handleDecertify]);

  const setActiveTab = useCallback((tab: TabKey) => {
    setActiveTabState(tab);
    window.history.pushState(null, "", `#${tab}`);
  }, []);

  useEffect(() => {
    const onPopState = () => setActiveTabState(getTabFromHash());
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const handleTabKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
      let newIndex: number | null = null;
      if (e.key === "ArrowRight") newIndex = (index + 1) % TABS.length;
      else if (e.key === "ArrowLeft")
        newIndex = (index - 1 + TABS.length) % TABS.length;
      else if (e.key === "Home") newIndex = 0;
      else if (e.key === "End") newIndex = TABS.length - 1;

      if (newIndex !== null) {
        e.preventDefault();
        const nextTab = tabRefs.current[newIndex];
        if (nextTab) {
          nextTab.focus();
          setActiveTab(TABS[newIndex].key);
        }
      }
    },
    [setActiveTab]
  );

  const isCurrentRedirect = certificationid === "current";

  useEffect(() => {
    if (isCurrentRedirect && certifications && certifications.length > 0) {
      navigate({
        to: "/$leaid/certification-cycles/$certificationid",
        params: {
          leaid,
          certificationid: String(certifications[0].ReportingCalendarId),
        },
        replace: true,
      });
    }
  }, [isCurrentRedirect, certifications, leaid, navigate]);

  if (isCurrentRedirect) {
    const noCycles = certifications && certifications.length === 0;
    return (
      <LayoutContentArea
        headingText={null}
        noBorder={true}
        showHeadingSection={false}
      >
        <Breadcrumb
          items={[
            {
              label: "Home",
              to: "/$leaid" as const,
              params: { leaid },
              ariaLabel: "Go to Home",
              isHome: true,
            },
            {
              label: "Certification Cycles",
              to: "/$leaid/certification-cycles" as const,
              params: { leaid },
            },
            { label: "Current Cycle" },
          ]}
        />
        <div
          className="d-flex flex-column justify-content-center align-items-center"
          style={{ minHeight: "400px" }}
          aria-busy={!noCycles}
          aria-live="polite"
        >
          {noCycles ? (
            <p className="text-muted">No certification cycles are available.</p>
          ) : (
            <>
              <Loader variant="primary" containerClass="mb-3" />
              <p className="text-muted">Loading current cycle...</p>
            </>
          )}
        </div>
      </LayoutContentArea>
    );
  }

  const breadcrumbItems = [
    {
      label: "Home",
      to: "/$leaid" as const,
      params: { leaid },
      ariaLabel: "Go to Home",
      isHome: true,
    },
    {
      label: "Certification Cycles",
      to: "/$leaid/certification-cycles" as const,
      params: { leaid },
    },
    { label: certificationMonth ?? "Loading..." },
  ];

  if (!certifications || certStats.isPending) {
    return (
      <LayoutContentArea
        headingText={null}
        noBorder={true}
        showHeadingSection={false}
      >
        <Breadcrumb items={breadcrumbItems} />
        <div
          className="d-flex flex-column justify-content-center align-items-center"
          style={{ minHeight: "400px" }}
          aria-busy="true"
          aria-live="polite"
        >
          <Loader variant="primary" containerClass="mb-3" />
          <p className="text-muted">Loading certification data...</p>
        </div>
      </LayoutContentArea>
    );
  }

  return (
    <LayoutContentArea
      headingText={null}
      noBorder={true}
      showHeadingSection={false}
    >
      <Breadcrumb items={breadcrumbItems} />
      <div
        className="card rounded-4 border-0 mb-4"
        style={{
          boxShadow:
            "0 1px 3px 0 rgb(15 23 42 / 0.08), 0 1px 2px -1px rgb(15 23 42 / 0.06)",
        }}
      >
        <div className="card-custom shadow-none p-0">
          <CertificationHeading
            certificationMonth={certificationMonth}
            certificationStatus={certificationStatus}
            certificationStep={certificationStep}
            lastReviewer={
              certification?.LastReviewer
                ? certification.LastReviewer
                : certification?.CertifierFirstName
                  ? `${certification.CertifierFirstName ?? ""} ${certification.CertifierLastName ?? ""}`.trim()
                  : "N/A"
            }
            lastReviewDate={
              certification?.LastReviewerDate
                ? formatReviewDate(certification.LastReviewerDate)
                : "N/A"
            }
            lastReviewDateString={
              certification?.LastReviewerDate
                ? (() => {
                    const raw = certification.LastReviewerDate;
                    const hasTimezone = /Z|[+-]\d{2}:?\d{2}$/.test(raw);
                    const d = new Date(hasTimezone ? raw : raw + "Z");
                    return isNaN(d.getTime())
                      ? "N/A"
                      : d.toLocaleString("en-US", {
                          month: "numeric",
                          day: "numeric",
                          year: "numeric",
                          hour: "numeric",
                          minute: "2-digit",
                          hour12: true,
                        });
                  })()
                : "N/A"
            }
            totalErrors={totalErrors}
            warnings={totalWarnings}
          />

          <div style={{ padding: "0 1.5rem" }}>
            <div
              role="tablist"
              aria-label="Certification sections"
              style={{
                display: "flex",
                borderBottom: "1px solid #cbd5e1",
                gap: "5px",
              }}
            >
              {TABS.map(({ key, label, icon: Icon }, index) => {
                const isActive = activeTab === key;
                return (
                  <button
                    key={key}
                    id={`tab-${key}`}
                    ref={(el) => {
                      tabRefs.current[index] = el;
                    }}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    aria-controls={`tabpanel-${key}`}
                    tabIndex={isActive ? 0 : -1}
                    onClick={() => setActiveTab(key)}
                    onKeyDown={(e) => handleTabKeyDown(e, index)}
                    onMouseEnter={(e) => {
                      if (!isActive) {
                        e.currentTarget.style.color = "#1e293b";
                        e.currentTarget.style.background = "#e2e8f0";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isActive) {
                        e.currentTarget.style.color = "#475569";
                        e.currentTarget.style.background = "#f1f5f9";
                      }
                    }}
                    style={{
                      flex: "1 1 0%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "0.5rem",
                      padding: "0.85rem 1rem",
                      fontSize: "0.875rem",
                      fontWeight: isActive ? 600 : 500,
                      color: isActive ? "#fff" : "#475569",
                      background: isActive ? "#003a5d" : "#f1f5f9",
                      border: isActive ? "none" : "1px solid #cbd5e1",
                      borderRadius: "10px 10px 0 0",
                      cursor: "pointer",
                      transition: "color 0.15s, background 0.15s",
                      marginBottom: "-1px",
                      borderBottom: isActive
                        ? "1px solid #1e293b"
                        : "1px solid #cbd5e1",
                      outline: "none",
                      boxShadow: "none",
                    }}
                  >
                    <Icon size={16} aria-hidden="true" />
                    {label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="px-4 pb-4">
            {TABS.map(({ key }) => {
              const isActive = activeTab === key;
              return (
                <div
                  key={key}
                  id={`tabpanel-${key}`}
                  role="tabpanel"
                  aria-labelledby={`tab-${key}`}
                  tabIndex={0}
                  hidden={!isActive}
                >
                  {key === "revisions" && isActive && (
                    <RevisionsTab
                      esiid={esiid}
                      esiidPath={leaid}
                      certificationId={certId}
                      lockedAnomalyRunId={lockedAnomalyRunId}
                      certificationAnomalyRun={
                        certification?.AnomalyRun ?? null
                      }
                      certificationStatus={
                        certificationStatus === "Certified"
                          ? "Certified"
                          : certificationStatus === "Decertified"
                            ? "Decertified"
                            : "Uncertified"
                      }
                      isCycleEnded={isCycleEnded}
                      canCertify={canCertify}
                    />
                  )}
                  {key === "validations" && isActive && (
                    <ValidationsTab
                      fatalErrors={totalErrors}
                      warnings={totalWarnings}
                      ssidErrors={ssidErrorsCount}
                      mids={midsCount}
                      discrepancies={discrepancies}
                      canEditRecord={canEditRecord}
                    />
                  )}
                  {key === "reports" && isActive && (
                    <ReportsTab
                      leaid={leaid}
                      reportingHistoryId={
                        lockedReportingHistoryId ?? mostRecentReportingHistoryId
                      }
                      reportMonthYear={reportMonthYear}
                      canCertify={canCertify}
                      canReviewReports={canReviewReports}
                      hasLockedRevision={hasLockedRevision}
                      mostRecentRevisionId={mostRecentRevisionId}
                      readSet={readSet}
                      persistedReadIds={persistedReadIds}
                      onReportRead={handleReportRead}
                      onReportUnread={handleReportUnread}
                      onAutoLock={handleAutoLock}
                      onReportViewed={handleReportViewed}
                      isCycleEnded={isCycleEnded}
                    />
                  )}
                  {key === "submission" && isActive && (
                    <SubmissionTab
                      leaName={leaContext.currentUserOrg.orgName ?? "—"}
                      vendorNumber={
                        leaContext.currentUserOrg.vendorNumber ?? "—"
                      }
                      certificationCycle={certificationMonth}
                      certificationStep={certificationStep}
                      certificationStatus={certificationStatus}
                      certifierFirstName={
                        certification?.CertifierFirstName ?? ""
                      }
                      certifierLastName={certification?.CertifierLastName ?? ""}
                      certifiedDate={
                        (certification as any)?.CertifiedDate ??
                        certification?.LastReviewerDate
                      }
                      isCycleActive={
                        certification?.WindowStart && certification?.WindowEnd
                          ? new Date() >=
                              startOfLocalDay(certification.WindowStart) &&
                            new Date() <=
                              endOfLocalDay(certification.WindowEnd)
                          : true
                      }
                      isCycleEnded={isCycleEnded}
                      canCertify={canCertify}
                      reportsReviewed={certLogsIsPending ? null : readSet.size}
                      totalReports={reportItems.length}
                      onCertify={handleCertify}
                      onDecertify={wrappedHandleDecertify}
                    />
                  )}
                  {key === "lifecycle" && isActive && (
                    <LifecycleTab
                      esiidPath={esiidPath}
                      certificationId={certId ?? ""}
                      reportIdToIndex={reportIdToIndex}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <style>{`
        [role="tab"]:focus-visible {
          box-shadow: 0 0 0 2px #fff, 0 0 0 4px #003a5d !important;
        }
        [role="tabpanel"]:focus {
          outline: none;
        }
      `}</style>
    </LayoutContentArea>
  );
}
